import React, { useCallback, useEffect, useState } from 'react';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import clinicService from '../../../services/clinic.service';
import { useNavigate, Link } from 'react-router-dom';
import UserReminder from '../../pages/settings/users/UserReminder';
import EcxUserProfileModal from './EcxUserProfileModal';
import './EcxOffcanvas.css';

const getInitials = (userInfo) => {
    const first = userInfo?.firstName?.[0] || '';
    const last = userInfo?.lastName?.[0] || '';
    const initials = `${first}${last}`.trim().toUpperCase();

    return initials || null;
};

const formatRoleLabel = (role) => {
    if (!role) {
        return null;
    }

    return role
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const UserStatusPills = ({ user }) => {
    if (!user) {
        return null;
    }

    return (
        <div className="ecx-user-panel__status-group">
            {user.isBlocked && (
                <span className="ecx-user-panel__status-pill is-closed">
                    <i className="bi bi-slash-circle" />
                    Blocked
                </span>
            )}
            <span className={`ecx-user-panel__status-pill ${user.isActive ? 'is-open' : 'is-closed'}`}>
                <i className={`bi ${user.isActive ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`} />
                {user.isActive ? 'Active' : 'Inactive'}
            </span>
            <span className={`ecx-user-panel__status-pill ${user.isAuthenticated ? 'is-active' : 'is-closed'}`}>
                <i className={`bi ${user.isAuthenticated ? 'bi-shield-check' : 'bi-clock'}`} />
                {user.isAuthenticated ? 'Authenticated' : 'Pending'}
            </span>
        </div>
    );
};

function EcxOffcanvas({ show, handleClose }) {
    const userInfo = useEyeconxStore((state) => state.userInfo);
    const setUserInfo = useEyeconxStore((state) => state.setUserInfo);
    const user = useEyeconxStore((state) => state.user);

    const navigate = useNavigate();
    const initials = getInitials(userInfo);
    const roleLabel = formatRoleLabel(userInfo?.role || user?.role);
    const isEcp = (userInfo?.role || user?.role) === 'ECP';

    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const refreshUserInfo = useCallback(() => {
        if (!user?.clinicId || !user?.ID) {
            return Promise.resolve();
        }

        setIsRefreshing(true);

        return clinicService
            .getUserInfo(user.clinicId, user.ID)
            .then((res) => setUserInfo(res.data))
            .catch(() => {})
            .finally(() => setIsRefreshing(false));
    }, [user?.clinicId, user?.ID, setUserInfo]);

    useEffect(() => {
        if (userInfo === null) {
            refreshUserInfo();
        }
    }, [userInfo, refreshUserInfo]);

    useEffect(() => {
        if (show) {
            refreshUserInfo();
        }
    }, [show, refreshUserInfo]);

    useEffect(() => {
        if (!show) {
            return undefined;
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                if (showProfileModal) {
                    setShowProfileModal(false);
                    return;
                }

                if (showReminderModal) {
                    setShowReminderModal(false);
                    return;
                }

                handleClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [show, handleClose, showProfileModal, showReminderModal]);

    const handleLogout = () => {
        handleClose();
        navigate('/logout');
    };

    const handleLinkClick = () => {
        handleClose();
    };

    const handleProfileSaved = (updatedUser) => {
        setUserInfo(updatedUser);
    };

    const reminderUser = userInfo?.id
        ? userInfo
        : user?.ID
            ? { id: user.ID, firstName: userInfo?.firstName, lastName: userInfo?.lastName }
            : null;

    return (
        <>
            {show && (
                <div
                    className="ecx-user-panel-backdrop"
                    onClick={handleClose}
                    aria-hidden="true"
                />
            )}

            <aside
                className={`offcanvas offcanvas-end ecx-user-panel ${show ? 'show' : ''}`}
                tabIndex="-1"
                style={{ visibility: show ? 'visible' : 'hidden' }}
                aria-hidden={!show}
                aria-label="Account panel"
            >
                <div className="ecx-user-panel__header">
                    <div className="ecx-user-panel__header-main">
                        <span className="ecx-user-panel__header-icon" aria-hidden="true">
                            <i className="bi bi-person-circle" />
                        </span>
                        <div className="min-width-0">
                            <h5 className="ecx-user-panel__title mb-0">My Account</h5>
                            <p className="ecx-user-panel__subtitle mb-0">Profile and security settings</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn flex-shrink-0"
                        aria-label="Close account panel"
                        onClick={handleClose}
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                <div className="ecx-user-panel__body ecx-page-scroll">
                    <section className="ecx-user-panel__profile-card">
                        <span className="ecx-user-panel__avatar" aria-hidden="true">
                            {initials || <i className="bi bi-person-fill" />}
                        </span>
                        <div className="ecx-user-panel__profile-content">
                            <h6 className="ecx-user-panel__name">
                                {userInfo?.firstName} {userInfo?.lastName}
                            </h6>
                            <div className="ecx-user-panel__profile-meta">
                                {roleLabel && (
                                    <span className="ecx-page-chip">
                                        <i className="bi bi-shield-check" />
                                        {roleLabel}
                                    </span>
                                )}
                                {isRefreshing && (
                                    <span className="ecx-user-panel__refreshing">
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                        Refreshing
                                    </span>
                                )}
                            </div>
                        </div>
                    </section>

                    <section className="ecx-user-panel__status-card">
                        <h6 className="ecx-user-panel__section-title">Account Status</h6>
                        <UserStatusPills user={userInfo} />
                    </section>

                    <section className="ecx-user-panel__details">
                        <div className="ecx-user-panel__detail">
                            <span className="ecx-user-panel__detail-label">Email</span>
                            <span className="ecx-user-panel__detail-value" title={userInfo?.email}>
                                <i className="bi bi-envelope" />
                                {userInfo?.email || '—'}
                            </span>
                        </div>
                    </section>

                    <section className="ecx-user-panel__section">
                        <h6 className="ecx-user-panel__section-title">Account Settings</h6>
                        <button
                            type="button"
                            className="ecx-user-panel__action ecx-page-list-item w-100 border-0 text-start"
                            onClick={() => setShowProfileModal(true)}
                        >
                            <span className="ecx-user-panel__action-icon">
                                <i className="bi bi-person-lines-fill" />
                            </span>
                            <span className="ecx-user-panel__action-copy">
                                <strong>Edit Profile</strong>
                                <small>Update your name and email</small>
                            </span>
                            <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                        </button>
                        {isEcp && (
                            <button
                                type="button"
                                className="ecx-user-panel__action ecx-page-list-item w-100 border-0 text-start"
                                onClick={() => setShowReminderModal(true)}
                            >
                                <span className="ecx-user-panel__action-icon">
                                    <i className="bi bi-bell" />
                                </span>
                                <span className="ecx-user-panel__action-copy">
                                    <strong>My Reminder Settings</strong>
                                    <small>Personal reminder preferences</small>
                                </span>
                                <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                            </button>
                        )}
                        <Link
                            to="/update-password"
                            className="ecx-user-panel__action ecx-page-list-item"
                            onClick={handleLinkClick}
                        >
                            <span className="ecx-user-panel__action-icon">
                                <i className="bi bi-shield-lock" />
                            </span>
                            <span className="ecx-user-panel__action-copy">
                                <strong>Password Settings</strong>
                                <small>Update your login password</small>
                            </span>
                            <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                        </Link>
                    </section>

                    <section className="ecx-user-panel__section">
                        <h6 className="ecx-user-panel__section-title">Support</h6>
                        <Link
                            to="/help"
                            className="ecx-user-panel__action ecx-page-list-item"
                            onClick={handleLinkClick}
                        >
                            <span className="ecx-user-panel__action-icon">
                                <i className="bi bi-question-circle" />
                            </span>
                            <span className="ecx-user-panel__action-copy">
                                <strong>Help &amp; Support</strong>
                                <small>Get help with EyeconX</small>
                            </span>
                            <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                        </Link>
                    </section>
                </div>

                <div className="ecx-user-panel__footer">
                    <button
                        type="button"
                        className="btn btn-outline-danger ecx-logout-btn w-100 py-2 d-flex align-items-center justify-content-center"
                        onClick={handleLogout}
                    >
                        <span>Logout</span>
                        <i className="bi bi-box-arrow-right ms-2" />
                    </button>
                </div>
            </aside>

            <EcxUserProfileModal
                show={showProfileModal}
                userInfo={userInfo}
                clinicId={user?.clinicId}
                userId={user?.ID}
                onClose={() => setShowProfileModal(false)}
                onSaved={handleProfileSaved}
            />

            {showReminderModal && (
                <UserReminder
                    show={showReminderModal}
                    user={reminderUser}
                    onClose={() => setShowReminderModal(false)}
                />
            )}
        </>
    );
}

export default EcxOffcanvas;
